import dayjs from 'dayjs'

import { descriptionsAreEqual } from './StoryMapDescriptionEditor'

const activityCacheByCardId = new Map()
const activitySubscribersByCardId = new Map()

const notifyActivitySubscribers = (cardId) => {
    const key = String(cardId)
    const cached = activityCacheByCardId.get(key)
    const activities = cached?.activities ?? []
    activitySubscribersByCardId.get(key)?.forEach((listener) => {
        listener(activities)
    })
}

export const getStoryMapActivityCache = (cardId) =>
    activityCacheByCardId.get(String(cardId))

export const setStoryMapActivityCache = (cardId, cache) => {
    activityCacheByCardId.set(String(cardId), cache)
}

export const subscribeStoryMapActivity = (cardId, listener) => {
    const key = String(cardId)
    if (!activitySubscribersByCardId.has(key)) {
        activitySubscribersByCardId.set(key, new Set())
    }
    activitySubscribersByCardId.get(key).add(listener)
    return () => {
        activitySubscribersByCardId.get(key)?.delete(listener)
    }
}

const normalizeAuthorKey = (value) => String(value ?? '').trim().toLowerCase()

const collectUserIdentityKeys = (user) => {
    const ids = new Set()
    const names = new Set()

    if (!user) return { ids, names }

    if (user._id) ids.add(String(user._id))
    if (user.id) ids.add(String(user.id))

    for (const key of [user.name, user.fullName, user.userName, user.username]) {
        const normalized = normalizeAuthorKey(key)
        if (normalized) names.add(normalized)
    }

    return { ids, names }
}

/** Show "You" only for the logged-in user's activity; otherwise the real author name. */
export const resolveActivityAuthorLabel = (item, currentUser) => {
    if (!item) return 'Someone'

    const { ids, names } = collectUserIdentityKeys(currentUser)
    const authorId = item.authorId ? String(item.authorId) : ''

    if (authorId && ids.has(authorId)) {
        return 'You'
    }

    const authorName = normalizeAuthorKey(item.authorName)
    if (authorName && names.has(authorName)) {
        return 'You'
    }

    if (authorName === 'you' && currentUser) {
        return 'You'
    }

    return item.authorName || 'Someone'
}

export const buildDescriptionActivityEntry = ({
    cardId,
    oldValue,
    newValue,
    authorName = 'Unknown',
    authorId = '',
    moduleType = 'story',
}) => {
    const now = dayjs()

    return {
        id: `local-desc-${cardId}-${now.valueOf()}`,
        description: '',
        action: 'updated',
        field: 'description',
        oldValue: oldValue ?? '',
        newValue: newValue ?? '',
        newAttachment: null,
        oldAttachment: null,
        content: null,
        moduleType,
        authorName,
        authorId: authorId ? String(authorId) : '',
        authorAvatar: '',
        createdAtRaw: now.valueOf(),
        dateKey: now.format('YYYY-MM-DD'),
        dateLabel: now.format('DD.MM.YYYY'),
        createdAt: now.format('DD.MM.YYYY, h:mm:ss A'),
        isLocal: true,
    }
}

/** Frontend-only activity log when description changes. */
export const prependStoryMapDescriptionActivity = ({
    cardId,
    oldValue,
    newValue,
    authorName = 'Unknown',
    authorId = '',
    moduleType = 'story',
}) => {
    if (!cardId) return null

    const previous = oldValue ?? ''
    const next = newValue ?? ''
    if (descriptionsAreEqual(previous, next)) return null

    const key = String(cardId)
    const entry = buildDescriptionActivityEntry({
        cardId: key,
        oldValue: previous,
        newValue: next,
        authorName,
        authorId,
        moduleType,
    })

    const cached = activityCacheByCardId.get(key) ?? {
        activities: [],
        hasMore: false,
        skip: 0,
    }

    const activities = [
        entry,
        ...cached.activities.filter((item) => item.id !== entry.id),
    ]

    activityCacheByCardId.set(key, {
        ...cached,
        activities,
        skip: Math.max(cached.skip ?? 0, activities.length),
    })

    notifyActivitySubscribers(key)
    return entry
}

export const mergeActivityWithLocal = (cardId, apiActivities = []) => {
    const key = String(cardId)
    const cached = activityCacheByCardId.get(key)
    const localEntries = (cached?.activities ?? []).filter((item) => item.isLocal)

    if (localEntries.length === 0) {
        return apiActivities
    }

    const apiIds = new Set(apiActivities.map((item) => item.id))
    const keptLocal = localEntries.filter((local) => {
        if (apiIds.has(local.id)) return false
        return !apiActivities.some(
            (api) =>
                api.field === 'description' &&
                String(api.newValue ?? '') === String(local.newValue ?? '') &&
                Math.abs((api.createdAtRaw ?? 0) - (local.createdAtRaw ?? 0)) < 60000
        )
    })

    return [...keptLocal, ...apiActivities]
}
